import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Col, Container, Form, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import neoLogo from "../../assets/images/Neo_Logo_C.svg";
import bgImage from "../../assets/images/pay.jpg";
import { functions, db } from "../../services/firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { LANDING_PLAN_CATALOG } from "./planCatalog";

const PAYMENT_METHODS = [
    {
        code: "PIX",
        label: "PIX",
        icon: "mdi-qrcode-scan",
        accent: "#00f3ff",
        description: "Pagamento instantaneo com QR Code ou copia e cola.",
    },
    {
        code: "CARD",
        label: "Cartao",
        icon: "mdi-credit-card-outline",
        accent: "#5cf7d4",
        description: "Checkout protegido do Mercado Pago com cartao de credito ou debito.",
    },
    {
        code: "BOLETO",
        label: "Boleto",
        icon: "mdi-barcode",
        accent: "#f59e0b",
        description: "Emissao de boleto com compensacao conforme o banco.",
    },
];

const PAYMENT_METHOD_FLAGS = {
    PIX: process.env.REACT_APP_MP_ENABLE_PIX !== "false",
    CARD: process.env.REACT_APP_MP_ENABLE_CARD !== "false",
    BOLETO: process.env.REACT_APP_MP_ENABLE_BOLETO !== "false",
};

const MERCADO_PAGO_SDK_URL = "https://sdk.mercadopago.com/js/v2";

const loadMercadoPagoSdk = () => new Promise((resolve, reject) => {
    if (window.MercadoPago) {
        resolve(window.MercadoPago);
        return;
    }

    const existingScript = document.querySelector(`script[src="${MERCADO_PAGO_SDK_URL}"]`);
    if (existingScript) {
        existingScript.addEventListener("load", () => resolve(window.MercadoPago));
        existingScript.addEventListener("error", () => reject(new Error("Nao foi possivel carregar o SDK do Mercado Pago.")));
        return;
    }

    const script = document.createElement("script");
    script.src = MERCADO_PAGO_SDK_URL;
    script.async = true;
    script.onload = () => resolve(window.MercadoPago);
    script.onerror = () => reject(new Error("Nao foi possivel carregar o SDK do Mercado Pago."));
    document.body.appendChild(script);
});

const getBrickPaymentMethods = (selectedMethod, installments) => {
    const normalized = String(selectedMethod || "PIX").toUpperCase();
    const paymentMethods = {
        maxInstallments: installments || 1,
    };

    if (normalized === "CARD") {
        paymentMethods.creditCard = "all";
        paymentMethods.debitCard = "all";
        paymentMethods.prepaidCard = "all";
        return paymentMethods;
    }

    if (normalized === "BOLETO") {
        paymentMethods.ticket = "all";
        return paymentMethods;
    }

    paymentMethods.bankTransfer = "all";
    return paymentMethods;
};

const initialFormData = {
    nome: "",
    documento: "",
    nomeEmpresa: "",
    email: "",
    telefone: "",
    senha: "",
};

const onlyNumbers = (value) => String(value || "").replace(/\D/g, "");

const CheckoutPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const incomingPlanCode = location.state?.planCode || searchParams.get("plan");
    const incomingLegacyPlan = location.state?.plan || null;

    const [dynamicPlans, setDynamicPlans] = useState([]);
    const [resolvedPlan, setResolvedPlan] = useState(null);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const q = query(collection(db, "platformPlans"), orderBy("sortOrder", "asc"));
                const snap = await getDocs(q);
                
                const plansList = snap.docs.map(doc => {
                    const data = doc.data();
                    const basePlanId = data.basePlanId || data.planId || "neo";
                    const price = Number(data.price || 0);
                    
                    const features = [];
                    const featureLabels = {
                        dashboard_operational: "Dashboard Operacional",
                        members_module: "Gestão de Alunos",
                        class_management: "Controle de Turmas",
                        team_management: "Gestão de Equipe",
                        dashboard_manager: "Dashboard Gerencial",
                        dashboard_financial: "Dashboard Financeiro",
                        member_financial_tab: "Mapeamento de Cobranças",
                        member_memberships_tab: "Vendas e Assinaturas",
                        sales_point: "Ponto de Venda (PDV)",
                        debt_payment: "Quitação de Débitos",
                        financial_module: "Módulo Financeiro (DRE/Caixa)",
                        crm_module: "CRM Integrado (Leads)",
                        goals_module: "Metas e Objetivos",
                        performance_module: "Performance de Equipe",
                        planning_module: "Planejamento Pedagógico",
                        gamification: "Gamificação (Rankings)",
                        automations: "Automações Cloud (WhatsApp)",
                        business_rules: "Regras de Negócio",
                        integrations: "Integrações Evolution",
                        kiosk: "Módulo Quiosque (Painel Piscina)",
                        ai_workout_generation: "Geração de Treino por IA"
                    };

                    Object.entries(data.features || data.effectiveFeatures || {}).forEach(([featKey, active]) => {
                        if (featureLabels[featKey]) {
                            features.push({ text: featureLabels[featKey], active });
                        }
                    });

                    if (features.length === 0) {
                        features.push({ text: "Acesso total a todas as funcionalidades", active: basePlanId === "neo" });
                        features.push({ text: "Gestão comercial e financeira", active: ["neo", "master"].includes(basePlanId) });
                        features.push({ text: "Automações e evolução de alunos", active: basePlanId === "neo" });
                    }

                    return {
                        id: doc.id,
                        code: data.code || doc.id,
                        title: data.title || data.name || doc.id,
                        headline: data.description || "Plano personalizado",
                        description: data.description || "",
                        price: price,
                        amount: price,
                        periodLabel: data.periodLabel || "/mês",
                        planId: basePlanId,
                        isTrial: data.code === "trial" || data.isTrial === true,
                        highlighted: data.highlighted === true,
                        installments: data.installments || (basePlanId === "neo" ? 12 : basePlanId === "master" ? 6 : 1),
                        features,
                    };
                });
                
                setDynamicPlans(plansList);
            } catch (err) {
                console.error("Erro ao carregar planos dinâmicos:", err);
            }
        };
        fetchPlans();
    }, []);

    useEffect(() => {
        let plan = null;
        const catalog = dynamicPlans.length > 0 ? dynamicPlans : LANDING_PLAN_CATALOG;

        if (incomingPlanCode) {
            plan = catalog.find(p => String(p.code).toLowerCase() === String(incomingPlanCode).toLowerCase() || String(p.id).toLowerCase() === String(incomingPlanCode).toLowerCase());
        }

        if (!plan && incomingLegacyPlan?.title) {
            plan = catalog.find(p => String(p.title).toLowerCase() === String(incomingLegacyPlan.title).toLowerCase());
        }

        if (!plan && dynamicPlans.length > 0) {
            plan = catalog.find(p => p.highlighted) || catalog[0];
        }

        if (!plan) {
            plan = LANDING_PLAN_CATALOG.find((p) => p.highlighted) || LANDING_PLAN_CATALOG[0];
        }

        setResolvedPlan(plan);
    }, [dynamicPlans, incomingPlanCode, incomingLegacyPlan]);

    const [step, setStep] = useState(1);
    const [paymentMethod, setPaymentMethod] = useState("PIX");
    const [tenantId, setTenantId] = useState("");
    const [formData, setFormData] = useState(initialFormData);
    const [loadingAccount, setLoadingAccount] = useState(false);
    const [loadingCheckout, setLoadingCheckout] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [brickLoading, setBrickLoading] = useState(false);
    const [brickError, setBrickError] = useState("");
    const [paymentResult, setPaymentResult] = useState(null);
    const brickControllerRef = useRef(null);

    // Auto-polling para verificar status do pagamento sem sair da tela
    useEffect(() => {
        let intervalId;
        
        if (paymentResult && paymentResult.status !== "approved" && paymentResult.externalReference) {
            const checkStatus = async () => {
                try {
                    const getPublicCheckoutStatus = httpsCallable(functions, "getPublicCheckoutStatus");
                    const response = await getPublicCheckoutStatus({
                        externalReference: paymentResult.externalReference,
                    });
                    
                    if (response.data && response.data.status === "approved") {
                        setPaymentResult(prev => ({ ...prev, status: "approved" }));
                        setSuccessMessage("Autenticando... Você será redirecionado em instantes.");
                        clearInterval(intervalId); // Para o polling

                        // Redireciona logo depois para o login (via hard-navigation para destravar cache e memory leaks do SDK do ML)
                        setTimeout(() => {
                            window.location.assign("/login");
                        }, 2500);
                    }
                } catch (error) {
                    console.error("Erro no polling automático:", error);
                }
            };
            
            // Fica checando a cada 4 segundos
            intervalId = setInterval(checkStatus, 4000);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [paymentResult, navigate]);
    const brickContainerId = "checkout-payment-brick";
    const mercadoPagoPublicKey = process.env.REACT_APP_MERCADO_PAGO_PUBLIC_KEY || "";
    const availablePaymentMethods = PAYMENT_METHODS.filter((method) => PAYMENT_METHOD_FLAGS[method.code] !== false);

    useEffect(() => {
        document.title = "Checkout | NeoApp";
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (!availablePaymentMethods.find((method) => method.code === paymentMethod) && availablePaymentMethods.length > 0) {
            setPaymentMethod(availablePaymentMethods[0].code);
        }
    }, [availablePaymentMethods, paymentMethod]);

    const priceLabel = resolvedPlan?.amount === 0
        ? "Gratuito por 30 dias"
        : `R$ ${resolvedPlan?.price}${resolvedPlan?.periodLabel || ""}`;

    const handleChange = ({ target: { name, value } }) => {
        setFormData((previous) => ({ ...previous, [name]: value }));
    };

    const handleCreateAccount = async (event) => {
        event.preventDefault();

        if (!resolvedPlan) {
            setErrorMessage("Nao foi possivel identificar o plano selecionado.");
            return;
        }

        setLoadingAccount(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            const payload = {
                name: formData.nomeEmpresa || formData.nome,
                tradeName: formData.nomeEmpresa || formData.nome,
                document: onlyNumbers(formData.documento),
                email: formData.email.trim(),
                phone: onlyNumbers(formData.telefone),
                ownerName: formData.nome,
                ownerEmail: formData.email.trim(),
                checkoutPlanCode: resolvedPlan.code,
                planId: resolvedPlan.planId,
                plan: resolvedPlan.planId,
                active: false,
                legalRepresentative: {
                    name: formData.nome,
                    cpf: onlyNumbers(formData.documento),
                    email: formData.email.trim(),
                    phone: onlyNumbers(formData.telefone),
                },
                responsible: {
                    name: formData.nome,
                    cpf: onlyNumbers(formData.documento),
                    email: formData.email.trim(),
                    phone: onlyNumbers(formData.telefone),
                    role: "Proprietario",
                },
            };

            const checkEmailExists = httpsCallable(functions, "checkEmailExists");
            const emailCheckResponse = await checkEmailExists({ email: formData.email.trim() });

            if (emailCheckResponse.data?.exists) {
                const ctx = emailCheckResponse.data.context;

                if (ctx === "inactive_tenant" || ctx === "expired_tenant") {
                    // Redirecionar para a página de reativação
                    navigate("/reactivate", { state: {
                        tenantId: emailCheckResponse.data.tenantId,
                        tenantName: emailCheckResponse.data.tenantName,
                        email: formData.email.trim(),
                        planId: emailCheckResponse.data.planId,
                        billingStatus: emailCheckResponse.data.billingStatus
                    }});
                    return;
                }

                if (ctx === "blocked_user") {
                    throw new Error("Sua conta foi bloqueada pelo administrador. Contate o suporte.");
                }

                throw new Error("Voce ja possui uma conta ativa. Faca login em /login para acessar o painel.");
            }

            const publicCheckout = httpsCallable(functions, "publicCheckout");
            const response = await publicCheckout({
                tenantData: payload,
                userData: {
                    name: formData.nome,
                    email: formData.email.trim(),
                    cpf: onlyNumbers(formData.documento),
                    phone: onlyNumbers(formData.telefone),
                    password: formData.senha,
                },
            });

            const createdTenantId = response.data?.idTenant || "";
            setTenantId(createdTenantId);
            setSuccessMessage("Conta criada com sucesso. Agora falta apenas concluir a etapa de pagamento.");
            setStep(2);
        } catch (error) {
            console.error("Erro ao criar conta no checkout:", error);
            setErrorMessage(error.message || "Nao foi possivel criar a conta inicial.");
        } finally {
            setLoadingAccount(false);
        }
    };

    const handleStartCheckout = async (event) => {
        event.preventDefault();

        if (!resolvedPlan || !tenantId) {
            setErrorMessage("Crie a conta antes de seguir para o pagamento.");
            return;
        }

        setLoadingCheckout(true);
        setErrorMessage("");
        setSuccessMessage("");

        try {
            const createPublicCheckoutPreference = httpsCallable(functions, "createPublicCheckoutPreference");
            const response = await createPublicCheckoutPreference({
                idTenant: tenantId,
                planCode: resolvedPlan.code,
                paymentMethod,
                originUrl: window.location.origin,
            });

            const data = response.data || {};

            if (data.redirectUrl) {
                window.location.assign(data.redirectUrl);
                return;
            }

            if (data.statusUrl) {
                navigate(data.statusUrl.replace(window.location.origin, ""));
                return;
            }

            throw new Error("Nao foi possivel obter a URL de pagamento.");
        } catch (error) {
            console.error("Erro ao iniciar checkout com Mercado Pago:", error);
            setErrorMessage(error.message || "Nao foi possivel iniciar o pagamento.");
        } finally {
            setLoadingCheckout(false);
        }
    };

    useEffect(() => {
        if (step !== 2 || resolvedPlan.isTrial || !tenantId) {
            return undefined;
        }

        if (!mercadoPagoPublicKey) {
            setBrickError("Defina REACT_APP_MERCADO_PAGO_PUBLIC_KEY para renderizar o checkout transparente.");
            return undefined;
        }

        let cancelled = false;

        const mountBrick = async () => {
            setBrickLoading(true);
            setBrickError("");

            try {
                await loadMercadoPagoSdk();

                if (cancelled) {
                    return;
                }

                if (brickControllerRef.current?.unmount) {
                    await brickControllerRef.current.unmount();
                }

                const mp = new window.MercadoPago(mercadoPagoPublicKey, { locale: "pt-BR" });
                const bricksBuilder = mp.bricks();

                const container = document.getElementById(brickContainerId);
                if (container) {
                    container.innerHTML = "";
                }

                brickControllerRef.current = await bricksBuilder.create("payment", brickContainerId, {
                    initialization: {
                        amount: Number(resolvedPlan.amount),
                        payer: {
                            email: formData.email.trim(),
                            entityType: "individual"
                        },
                    },
                    customization: {
                        visual: {
                            style: {
                                theme: "dark",
                            },
                        },
                        paymentMethods: getBrickPaymentMethods(paymentMethod, resolvedPlan.installments),
                    },
                    callbacks: {
                        onReady: () => {
                            if (!cancelled) {
                                setBrickLoading(false);
                            }
                        },
                        onSubmit: async (brickState) => {
                            setLoadingCheckout(true);
                            setErrorMessage("");
                            setSuccessMessage("");
                            setPaymentResult(null);

                            try {
                                const processPublicCheckoutPayment = httpsCallable(functions, "processPublicCheckoutPayment");
                                const response = await processPublicCheckoutPayment({
                                    idTenant: tenantId,
                                    planCode: resolvedPlan.code,
                                    formData: brickState?.formData || brickState || {},
                                    originUrl: window.location.origin,
                                });

                                const payment = response.data?.payment || null;
                                setPaymentResult(payment);

                                if (payment?.status === "approved") {
                                    setSuccessMessage("Pagamento aprovado. Sua conta ja pode ser acessada.");
                                } else if (payment?.status) {
                                    setSuccessMessage("Pagamento enviado. Confira abaixo as instrucoes do meio escolhido.");
                                }

                                // Remove imediatamente o componente nativo do ML para que a tela escura 
                                // (backdrop/mask) que eles injetam suma e exibamos o nosso próprio layout
                                if (brickControllerRef.current?.unmount) {
                                    brickControllerRef.current.unmount().catch(() => {});
                                    brickControllerRef.current = null;
                                }

                                return response.data;
                            } catch (error) {
                                console.error("Erro ao processar pagamento transparente:", error);
                                setErrorMessage(error.message || "Nao foi possivel processar o pagamento.");
                                throw error;
                            } finally {
                                setLoadingCheckout(false);
                            }
                        },
                        onError: (error) => {
                            console.error("Erro no Payment Brick do Mercado Pago:", error);
                            if (!cancelled) {
                                setBrickLoading(false);
                                setBrickError("Nao foi possivel carregar o formulario embutido do Mercado Pago.");
                            }
                        },
                    },
                });
            } catch (error) {
                console.error("Erro ao montar Payment Brick:", error);
                if (!cancelled) {
                    setBrickError(error.message || "Nao foi possivel iniciar o checkout transparente.");
                    setBrickLoading(false);
                }
            }
        };

        mountBrick();

        return () => {
            cancelled = true;
            if (brickControllerRef.current?.unmount) {
                brickControllerRef.current.unmount().catch(() => { });
            }
        };
    }, [
        step,
        tenantId,
        resolvedPlan,
        paymentMethod,
        mercadoPagoPublicKey,
        formData.email,
    ]);

    if (!resolvedPlan) {
        return null;
    }

    return (
        <div
            className="landing-page-container"
            style={{
                minHeight: "100vh",
                backgroundColor: "#0f1118",
                backgroundImage: `linear-gradient(135deg, rgba(6, 8, 14, 0.95) 0%, rgba(15, 17, 24, 0.88) 45%, rgba(8, 25, 35, 0.82) 100%), url(${bgImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                overflowX: "hidden",
            }}
        >
            <Container fluid className="px-4 px-xl-5 py-4 py-lg-5">
                <div className="d-flex justify-content-between align-items-center mb-4 mb-lg-5">
                    <Link to="/">
                        <img
                            src={neoLogo}
                            alt="NeoApp"
                            height="36"
                            style={{ filter: "brightness(0) invert(1)" }}
                        />
                    </Link>

                    <Link
                        to="/"
                        className="text-decoration-none"
                        style={{
                            color: "#dbe4ff",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            fontSize: "0.8rem",
                        }}
                    >
                        Voltar para os planos
                    </Link>
                </div>

                <Row className="g-4 align-items-stretch">
                    <Col lg={7} xl={6}>
                        <div className="checkout-shell h-100">
                            <div className="checkout-shell-outline">CHECKOUT</div>

                            <div className="d-flex flex-wrap align-items-center gap-3 mb-4">
                                <div className="checkout-step-chip">{`Etapa ${step} de 2`}</div>
                                <div style={{ color: "#8d9ab9", fontSize: "0.92rem" }}>
                                    Cadastre-se + cobrança segura via Mercado Pago
                                </div>
                            </div>

                            <h1 className="checkout-title">
                                {step === 1 ? "Crie sua conta" : resolvedPlan.isTrial ? "Ative seu teste" : "Finalize sua assinatura"}
                            </h1>
                            <p className="checkout-subtitle">
                                {step === 1
                                    ? "Vamos criar a empresa e o usuario proprietario para preparar o ambiente do seu tenant."
                                    : resolvedPlan.isTrial
                                        ? "Seu tenant ja esta pronto. Agora vamos liberar o trial e concluir a entrada no sistema."
                                        : "O pagamento acontece em ambiente seguro do Mercado Pago, sem armazenar dados sensiveis no NeoApp."}
                            </p>

                            {errorMessage ? <Alert color="danger" fade={false}>{errorMessage}</Alert> : null}
                            {successMessage ? <Alert color="success" fade={false}>{successMessage}</Alert> : null}

                            {step === 1 ? (
                                <Form onSubmit={handleCreateAccount}>
                                    <Row>
                                        <Col md={12}>
                                            <FormGroup className="mb-4">
                                                <Label className="checkout-label">Nome completo</Label>
                                                <Input className="checkout-input" name="nome" value={formData.nome} onChange={handleChange} required />
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup className="mb-4">
                                                <Label className="checkout-label">CPF ou CNPJ</Label>
                                                <Input className="checkout-input" name="documento" value={formData.documento} onChange={handleChange} required />
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup className="mb-4">
                                                <Label className="checkout-label">Empresa ou estudio</Label>
                                                <Input className="checkout-input" name="nomeEmpresa" value={formData.nomeEmpresa} onChange={handleChange} />
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup className="mb-4">
                                                <Label className="checkout-label">E-mail</Label>
                                                <Input className="checkout-input" type="email" name="email" value={formData.email} onChange={handleChange} required />
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup className="mb-4">
                                                <Label className="checkout-label">WhatsApp</Label>
                                                <Input className="checkout-input" name="telefone" value={formData.telefone} onChange={handleChange} required />
                                            </FormGroup>
                                        </Col>

                                        <Col md={12}>
                                            <FormGroup className="mb-4">
                                                <Label className="checkout-label">Senha inicial</Label>
                                                <Input className="checkout-input" type="password" name="senha" value={formData.senha} onChange={handleChange} required />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <button type="submit" className="checkout-primary-btn w-100" disabled={loadingAccount}>
                                        {loadingAccount ? <Spinner size="sm" /> : "Criar conta e continuar"}
                                    </button>
                                </Form>
                            ) : resolvedPlan.isTrial ? (
                                <Form onSubmit={handleStartCheckout}>
                                    <div className="checkout-summary-card mb-4">
                                        <div>
                                            <span>Plano selecionado</span>
                                            <strong>{resolvedPlan.title}</strong>
                                            <p>{resolvedPlan.headline}</p>
                                        </div>

                                        <div className="text-lg-end">
                                            <div className="checkout-price">{priceLabel}</div>
                                            <small>{resolvedPlan.isTrial ? "Sem cobranca imediata" : "Pagamento via Mercado Pago"}</small>
                                        </div>
                                    </div>

                                    {!resolvedPlan.isTrial ? (
                                        <React.Fragment>
                                            <Label className="checkout-label mb-3">Como deseja pagar</Label>
                                            <div className="payment-method-grid mb-4">
                                                {PAYMENT_METHODS.map((method) => (
                                                    <button
                                                        key={method.code}
                                                        type="button"
                                                        className={`payment-method-card ${paymentMethod === method.code ? "active" : ""}`}
                                                        onClick={() => setPaymentMethod(method.code)}
                                                    >
                                                        <i className={`mdi ${method.icon}`} style={{ color: method.accent }} />
                                                        <strong>{method.label}</strong>
                                                        <span>{method.description}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </React.Fragment>
                                    ) : null}

                                    <div className="checkout-note mb-4">
                                        <i className="mdi mdi-shield-check-outline" />
                                        <div>
                                            <strong>Fluxo seguro</strong>
                                            <p className="mb-0">
                                                {resolvedPlan.isTrial
                                                    ? "O trial ativa sua empresa sem coletar cartao no NeoApp."
                                                    : "Depois do clique, voce sera redirecionado para o checkout seguro do Mercado Pago."}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="d-flex flex-wrap gap-3">
                                        <button type="button" className="checkout-secondary-btn" onClick={() => setStep(1)}>
                                            Voltar aos dados
                                        </button>
                                        <button type="submit" className="checkout-primary-btn flex-grow-1" disabled={loadingCheckout}>
                                            {loadingCheckout
                                                ? <Spinner size="sm" />
                                                : resolvedPlan.isTrial
                                                    ? "Liberar 30 dias gratis"
                                                    : "Ir para o Mercado Pago"}
                                        </button>
                                    </div>
                                </Form>
                            ) : (
                                <div>
                                    <div className="checkout-summary-card mb-4">
                                        <div>
                                            <span>Plano selecionado</span>
                                            <strong>{resolvedPlan.title}</strong>
                                            <p>{resolvedPlan.headline}</p>
                                        </div>

                                        <div className="text-lg-end">
                                            <div className="checkout-price">{priceLabel}</div>
                                            <small>Checkout transparente Mercado Pago</small>
                                        </div>
                                    </div>

                                    <Label className="checkout-label mb-3">Forma de pagamento</Label>
                                    <div className="payment-method-grid mb-4">
                                        {availablePaymentMethods.map((method) => (
                                            <button
                                                key={method.code}
                                                type="button"
                                                className={`payment-method-card ${paymentMethod === method.code ? "active" : ""}`}
                                                onClick={() => setPaymentMethod(method.code)}
                                            >
                                                <i className={`mdi ${method.icon}`} style={{ color: method.accent }} />
                                                <strong>{method.label}</strong>
                                                <span>{method.description}</span>
                                            </button>
                                        ))}
                                    </div>

                                    <div className="checkout-note mb-4">
                                        <i className="mdi mdi-shield-check-outline" />
                                        <div>
                                            <strong>Pagamento sem redirecionamento</strong>
                                            <p className="mb-0">
                                                O formulario abaixo e renderizado pelo Mercado Pago dentro desta pagina e envia o pagamento direto para o backend do NeoApp.
                                            </p>
                                        </div>
                                    </div>

                                    {availablePaymentMethods.length === 0 ? (
                                        <Alert color="warning" fade={false}>
                                            Nenhum meio de pagamento foi habilitado neste ambiente. Revise as variaveis `REACT_APP_MP_ENABLE_*`.
                                        </Alert>
                                    ) : null}

                                    {brickError ? <Alert color="danger" fade={false}>{brickError}</Alert> : null}

                                    <div className="checkout-brick-shell mb-4" style={{ display: paymentResult ? "none" : "block" }}>
                                        {brickLoading ? (
                                            <div className="text-center py-4">
                                                <Spinner color="light" />
                                                <p className="mb-0 mt-3" style={{ color: "#b9c2d8" }}>
                                                    Carregando formulario seguro do Mercado Pago...
                                                </p>
                                            </div>
                                        ) : null}
                                        <div id={brickContainerId} />
                                    </div>

                                    {paymentResult ? (
                                        <div className="checkout-payment-result mb-4">
                                            <span>Status da cobranca</span>
                                            <strong>{paymentResult.status === "approved" ? "Aprovado" : "Pendente de Pagamento"}</strong>
                                            
                                            {paymentResult.qr_code_base64 && (
                                                <div className="text-center mt-4">
                                                    <p className="text-white mb-3 fw-semibold">
                                                        Escaneie o QR Code abaixo para pagar via PIX:
                                                    </p>
                                                    <div style={{ background: "white", padding: "16px", borderRadius: "16px", display: "inline-block", marginBottom: "16px" }}>
                                                        <img
                                                            src={`data:image/png;base64,${paymentResult.qr_code_base64}`}
                                                            alt="QR Code PIX"
                                                            style={{ width: "200px", maxWidth: "100%", display: "block" }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {paymentResult.qr_code && (
                                                <div className="mt-2 mb-3">
                                                    <p className="text-white mb-2 fw-semibold" style={{ fontSize: "0.95rem" }}>
                                                        Ou utilize a opção PIX Copia e Cola:
                                                    </p>
                                                    <div className="checkout-code-box">
                                                        {paymentResult.qr_code}
                                                    </div>
                                                    <div className="mt-4 p-3 rounded-3" style={{ background: "rgba(0, 243, 255, 0.05)", border: "1px solid rgba(0, 243, 255, 0.15)" }}>
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <i className="mdi mdi-clock-fast text-info fs-5"></i>
                                                            <strong className="text-info" style={{ fontSize: "0.95rem" }}>Confirmação Automática</strong>
                                                        </div>
                                                        <p className="mb-0 text-white-50" style={{ fontSize: "0.85rem", lineHeight: "1.6" }}>
                                                            Após realizar o pagamento, nosso sistema irá identificar a transação automaticamente. Você receberá um e-mail de confirmação e as instruções de acesso.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {paymentResult.ticket_url && (
                                                <div className="mt-3">
                                                    <a
                                                        href={paymentResult.ticket_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="checkout-primary-btn text-decoration-none w-100"
                                                    >
                                                        Visualizar Boleto
                                                    </a>
                                                    <div className="mt-3 p-3 rounded-3" style={{ background: "rgba(245, 158, 11, 0.05)", border: "1px solid rgba(245, 158, 11, 0.15)" }}>
                                                        <div className="d-flex align-items-center gap-2 mb-2">
                                                            <i className="mdi mdi-clock-fast text-warning fs-5"></i>
                                                            <strong className="text-warning" style={{ fontSize: "0.95rem" }}>Compensação Bancária</strong>
                                                        </div>
                                                        <p className="mb-0 text-white-50" style={{ fontSize: "0.85rem", lineHeight: "1.6" }}>
                                                            Pagamentos via boleto podem demorar até 2 dias úteis para serem compensados. O acesso será liberado automaticamente após a compensação.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : null}

                                    <div className="d-flex flex-wrap gap-3">
                                        <button type="button" className="checkout-secondary-btn" onClick={() => setStep(1)}>
                                            Voltar aos dados
                                        </button>
                                        {paymentResult?.externalReference ? (
                                            <div className="d-flex align-items-center gap-2">
                                                {paymentResult.status !== "approved" ? (
                                                    <React.Fragment>
                                                        <Spinner size="sm" color="info" />
                                                        <span style={{ color: "#8d9ab9", fontSize: "0.85rem" }}>
                                                            Aguardando o sistema identificar o pagamento automático...
                                                        </span>
                                                    </React.Fragment>
                                                ) : (
                                                    <span style={{ color: "#22c55e", fontSize: "0.95rem", fontWeight: "bold" }}>
                                                        <i className="mdi mdi-check-circle me-1"></i>
                                                        Pagamento identificado com sucesso!
                                                    </span>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Col>

                    <Col lg={5} xl={6}>
                        <div className="checkout-aside h-100">
                            <div className="checkout-plan-badge">
                                {resolvedPlan.highlighted ? "Plano recomendado" : "Plano selecionado"}
                            </div>

                            <h2 className="checkout-aside-title">{resolvedPlan.title}</h2>
                            <p className="checkout-aside-copy">{resolvedPlan.description}</p>

                            <div className="checkout-aside-price">{priceLabel}</div>

                            <div className="checkout-feature-list">
                                {resolvedPlan.features.map((feature) => (
                                    <div key={feature.text} className="checkout-feature-item">
                                        <i className={`mdi ${feature.active ? "mdi-check-circle" : "mdi-close-circle-outline"}`} />
                                        <span>{feature.text}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="checkout-support-card">
                                <span>O que acontece depois</span>
                                <strong>{resolvedPlan.isTrial ? "Acesso imediato apos a ativacao" : "Seu acesso é liberado assim que o pagamento for aprovado"}</strong>

                            </div>
                        </div>
                    </Col>
                </Row>

                <div className="text-center mt-5 pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                    <img 
                        src={neoLogo} 
                        alt="NeoApp / Intelitec" 
                        height="24" 
                        style={{ filter: "brightness(0) invert(1)", opacity: 0.4, marginBottom: "12px" }} 
                    />
                    <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem", lineHeight: "1.6", margin: 0 }}>
                        Ambiente de checkout 100% seguro.<br/>
                        <strong>Intelitec Inteligência Tecnológica Ltda</strong> — CNPJ: 60.180.850/0001-40
                    </p>
                </div>
            </Container>

            <style>
                {`
                    .checkout-shell,
                    .checkout-aside {
                        position: relative;
                        border-radius: 30px;
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        background: rgba(16, 19, 29, 0.84);
                        backdrop-filter: blur(18px);
                        box-shadow: 0 30px 90px rgba(0, 0, 0, 0.35);
                        overflow: hidden;
                    }

                    .checkout-shell {
                        padding: clamp(24px, 3vw, 40px);
                    }

                    .checkout-shell-outline {
                        position: absolute;
                        right: 16px;
                        top: 12px;
                        color: transparent;
                        -webkit-text-stroke: 1px rgba(0, 243, 255, 0.12);
                        font-size: clamp(3.8rem, 7vw, 7rem);
                        font-weight: 800;
                        letter-spacing: 4px;
                        pointer-events: none;
                    }

                    .checkout-step-chip {
                        background: rgba(0, 243, 255, 0.1);
                        color: #00f3ff;
                        padding: 8px 14px;
                        border-radius: 999px;
                        text-transform: uppercase;
                        letter-spacing: 1.1px;
                        font-weight: 700;
                        font-size: 0.75rem;
                    }

                    .checkout-title {
                        color: #ffffff;
                        font-size: clamp(2.1rem, 4vw, 3.3rem);
                        font-weight: 700;
                        line-height: 1.1;
                        margin-bottom: 14px;
                        letter-spacing: -1px;
                    }

                    .checkout-subtitle {
                        color: #b9c2d8;
                        font-size: 1rem;
                        line-height: 1.8;
                        margin-bottom: 32px;
                        max-width: 720px;
                    }

                    .checkout-label {
                        color: #00f3ff;
                        font-size: 0.8rem;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        font-weight: 700;
                        margin-bottom: 10px;
                    }

                    .checkout-input {
                        min-height: 54px;
                        border-radius: 14px;
                        background: rgba(7, 10, 18, 0.5) !important;
                        border: 1px solid rgba(255, 255, 255, 0.1) !important;
                        color: #ffffff !important;
                        padding: 14px 16px !important;
                    }

                    .checkout-input:focus {
                        border-color: rgba(0, 243, 255, 0.9) !important;
                        box-shadow: 0 0 0 3px rgba(0, 243, 255, 0.12) !important;
                    }

                    .checkout-primary-btn,
                    .checkout-secondary-btn {
                        min-height: 56px;
                        border-radius: 16px;
                        padding: 0 22px;
                        border: 1px solid transparent;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 10px;
                        font-weight: 700;
                        letter-spacing: 0.4px;
                        transition: all 0.2s ease;
                    }

                    .checkout-primary-btn {
                        background: linear-gradient(135deg, #00f3ff 0%, #5cf7d4 100%);
                        color: #07131f;
                    }

                    .checkout-secondary-btn {
                        background: transparent;
                        color: #ffffff;
                        border-color: rgba(255, 255, 255, 0.14);
                    }

                    .checkout-primary-btn:hover,
                    .checkout-secondary-btn:hover,
                    .payment-method-card:hover {
                        transform: translateY(-1px);
                    }

                    .checkout-summary-card,
                    .checkout-support-card,
                    .checkout-note,
                    .checkout-payment-result,
                    .checkout-brick-shell {
                        background: rgba(255, 255, 255, 0.03);
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        border-radius: 20px;
                    }

                    .checkout-summary-card {
                        display: flex;
                        justify-content: space-between;
                        gap: 18px;
                        padding: 20px 24px;
                    }

                    .checkout-support-card {
                        padding: 20px 24px;
                    }

                    .checkout-summary-card span,
                    .checkout-support-card span {
                        display: block;
                        color: #8d9ab9;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        font-size: 0.75rem;
                        margin-bottom: 8px;
                    }

                    .checkout-summary-card strong,
                    .checkout-support-card strong {
                        color: #ffffff;
                        display: block;
                        font-size: 1.1rem;
                        margin-bottom: 8px;
                    }

                    .checkout-summary-card p,
                    .checkout-support-card p {
                        color: #b9c2d8;
                        line-height: 1.6;
                    }

                    .checkout-price,
                    .checkout-aside-price {
                        color: #00f3ff;
                        font-size: clamp(1.8rem, 4vw, 2.6rem);
                        font-weight: 700;
                    }

                    .payment-method-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
                        gap: 14px;
                    }

                    .payment-method-card {
                        background: rgba(255, 255, 255, 0.03);
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        border-radius: 18px;
                        min-height: 146px;
                        text-align: left;
                        padding: 18px;
                        color: #ffffff;
                        transition: all 0.2s ease;
                    }

                    .payment-method-card.active {
                        border-color: rgba(0, 243, 255, 0.9);
                        box-shadow: 0 0 0 3px rgba(0, 243, 255, 0.12);
                        background: rgba(0, 243, 255, 0.07);
                    }

                    .payment-method-card i {
                        display: block;
                        font-size: 1.7rem;
                        margin-bottom: 12px;
                    }

                    .payment-method-card strong {
                        display: block;
                        font-size: 1rem;
                        margin-bottom: 8px;
                    }

                    .payment-method-card span {
                        display: block;
                        color: #b9c2d8;
                        font-size: 0.88rem;
                        line-height: 1.6;
                    }

                    .checkout-note {
                        display: flex;
                        gap: 14px;
                        align-items: flex-start;
                        padding: 18px 20px;
                    }

                    .checkout-note i {
                        color: #22c55e;
                        font-size: 1.5rem;
                    }

                    .checkout-note strong {
                        color: #ffffff;
                        display: block;
                        margin-bottom: 6px;
                    }

                    .checkout-note p {
                        color: #b9c2d8;
                        line-height: 1.6;
                    }

                    .checkout-brick-shell {
                        padding: 20px;
                    }

                    .checkout-payment-result {
                        padding: 20px;
                    }

                    .checkout-payment-result span {
                        display: block;
                        color: #8d9ab9;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        font-size: 0.75rem;
                        margin-bottom: 10px;
                    }

                    .checkout-payment-result strong {
                        color: #ffffff;
                        font-size: 1.15rem;
                    }

                    .checkout-code-box {
                        margin-top: 12px;
                        padding: 14px;
                        border-radius: 14px;
                        background: rgba(7, 10, 18, 0.55);
                        color: #dffbff;
                        font-size: 0.88rem;
                        line-height: 1.7;
                        word-break: break-all;
                    }

                    .checkout-aside {
                        padding: clamp(24px, 3vw, 38px);
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                    }

                    .checkout-plan-badge {
                        display: inline-flex;
                        align-items: center;
                        width: fit-content;
                        background: rgba(255, 255, 255, 0.06);
                        color: #00f3ff;
                        padding: 9px 14px;
                        border-radius: 999px;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        font-size: 0.74rem;
                        margin-bottom: 22px;
                    }

                    .checkout-aside-title {
                        color: #ffffff;
                        font-size: clamp(2rem, 4vw, 3rem);
                        font-weight: 700;
                        margin-bottom: 12px;
                    }

                    .checkout-aside-copy {
                        color: #d0d8eb;
                        font-size: 1rem;
                        line-height: 1.8;
                        margin-bottom: 24px;
                    }

                    .checkout-feature-list {
                        display: grid;
                        gap: 12px;
                        margin: 28px 0;
                    }

                    .checkout-feature-item {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        color: #e8eefb;
                        line-height: 1.6;
                    }

                    .checkout-feature-item i {
                        color: #00f3ff;
                        font-size: 1.1rem;
                    }

                    @media (max-width: 767px) {
                        .checkout-summary-card {
                            flex-direction: column;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default CheckoutPage;
